import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api, { isAuthed, getUser, errMsg } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import {
  Cigarette, PawPrint, PartyPopper, Baby, Clock, Volume2,
  KeyRound, User, MapPin, Phone, Mail, CalendarClock, ShieldCheck, ShieldAlert, AlertCircle, Check, X, Ban, Gift, Trophy, ArrowUpRight, Map, Send
} from "lucide-react";

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-border last:border-0">
      <Icon className="w-4 h-4 text-primary mt-0.5 shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-sm font-medium mt-0.5">{value || "—"}</div>
      </div>
    </div>
  );
}

function BoolBadge({ ok, label, negOk }) {
  const Icon = ok ? Check : X;
  const cls = negOk
    ? (ok ? "bg-sage/10 text-sage border-sage/40" : "bg-destructive/10 text-destructive border-destructive/40")
    : (ok ? "bg-destructive/10 text-destructive border-destructive/40" : "bg-sage/10 text-sage border-sage/40");
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${cls}`}>
      <Icon className="w-3.5 h-3.5"/>{label}
    </span>
  );
}

export default function GuestPortal() {
  const { session_id } = useParams();
  const { lang } = useLang();
  const IT = lang === "it";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirectTo, setRedirectTo] = useState(null);

  const authedGuest = isAuthed() && getUser()?.role === "guest" ? getUser() : null;

  useEffect(() => {
    api.get(`/bookings/${session_id}/guest`)
      .then((r) => {
        // Notify the host that the guest has just opened their portal —
        // strong signal they're actively planning the trip. Deduped server-
        // side (portal_opened_at) so reloads don't spam. Fire-and-forget.
        api.post(`/guest-portal/${session_id}/opened`).catch(() => {});
        // If the logged-in guest owns this booking, forward to the unified authed detail page.
        // Anonymous or mismatched-email visitors still see the classic per-booking portal.
        const b = r.data?.booking || {};
        if (authedGuest && b.id && (b.guest_email || "").toLowerCase() === (authedGuest.email || "").toLowerCase()) {
          setRedirectTo(`/guest/dashboard/booking/${b.id}`);
          return;
        }
        setData(r.data);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Errore"))
      .finally(() => setLoading(false));
  }, [session_id, authedGuest]);

  if (redirectTo) return <Navigate to={redirectTo} replace/>;

  if (loading) return <div className="min-h-screen bg-background"><Navbar/><div className="text-center py-20 text-muted-foreground">…</div></div>;
  if (error) return (
    <div className="min-h-screen bg-background"><Navbar/>
      <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="guest-portal-error">
        <AlertCircle className="w-14 h-14 text-destructive mx-auto mb-4"/>
        <h1 className="font-display text-3xl mb-2">{IT ? "Accesso non disponibile" : "Access unavailable"}</h1>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    </div>
  );

  const b = data.booking;  const p = data.property;
  const rules = p.house_rules || {};
  const access = p.access_instructions || {};

  return (
    <div className="min-h-screen bg-background">
      <Navbar/>
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="guest-portal">
        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-sage/10 text-sage rounded-full px-3 py-1 mb-3">
          <ShieldCheck className="w-3 h-3"/> {IT ? "Prenotazione confermata" : "Booking confirmed"}
        </div>
        <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
          <h1 className="font-display text-4xl sm:text-5xl">{IT ? `Benvenuto, ${b.guest_name || ""}` : `Welcome, ${b.guest_name || ""}`}</h1>
          <a href="/caccia-al-tesoro" data-testid="guest-nav-map"
             className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all lift">
            <Map className="w-4 h-4"/> {IT ? "Mappa del Tesoro" : "Treasure Map"}
          </a>
        </div>
        <p className="text-muted-foreground mb-8">
          {IT ? "Ecco tutto ciò che ti serve per il tuo soggiorno presso" : "Here's everything you need for your stay at"} <b className="text-foreground">{p.name}</b>, {p.city}.
        </p>

        {/* Treasure Hunt — headline experience */}
        <WelcomeSeedCard city={p.city} authedGuest={authedGuest} IT={IT} guestName={b.guest_name}/>

        {/* Booking recap */}
        <div className="border border-border rounded-lg p-5 bg-card mb-6" data-testid="guest-booking-recap">
          <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-3">
            {IT ? "La tua prenotazione" : "Your booking"}
          </div>
          <Row icon={CalendarClock} label={IT ? "Check-in" : "Check-in"} value={b.check_in}/>
          <Row icon={CalendarClock} label={IT ? "Check-out" : "Check-out"} value={b.check_out}/>
          <Row icon={User} label={IT ? "Camera" : "Room"} value={b.room_name}/>
          <Row icon={User} label={IT ? "Ospiti" : "Guests"} value={b.guests}/>
        </div>

        <FreeCancelCard booking={b} sessionId={session_id} IT={IT} onCancelled={() => window.location.reload()}/>
        <EtaCard booking={b} sessionId={session_id} IT={IT}/>

        {/* Access instructions */}
        <div className="border border-primary/40 rounded-lg p-5 bg-primary/5 mb-6" data-testid="guest-access-instructions">
          <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-3 flex items-center gap-1">
            <KeyRound className="w-3.5 h-3.5"/>{IT ? "Istruzioni per l'accesso" : "Access instructions"}
          </div>
          {access.type === "self" ? (
            <>
              <div className="text-lg font-display mb-3">{IT ? "Self check-in" : "Self check-in"}</div>
              {access.self_code && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{IT ? "Codice porta/cassaforte" : "Door/safe code"}</div>
                  <div className="font-mono text-3xl tracking-widest text-primary mt-1" data-testid="guest-self-code">{access.self_code}</div>
                </div>
              )}
              {access.self_lockbox_location && <Row icon={MapPin} label={IT ? "Posizione keybox" : "Keybox location"} value={access.self_lockbox_location}/>}
              {access.self_photo && (
                <img src={access.self_photo} alt="keybox" className="mt-3 rounded-md max-h-64 object-cover" data-testid="guest-self-photo"/>
              )}
            </>
          ) : (
            <>
              <div className="text-lg font-display mb-3">{IT ? "Check-in di persona" : "In-person check-in"}</div>
              {access.contact_name && <Row icon={User} label={IT ? "Referente" : "Contact"} value={access.contact_name}/>}
              {access.contact_phone && (
                <Row icon={Phone} label={IT ? "Telefono" : "Phone"} value={
                  <a href={`tel:${access.contact_phone}`} className="text-primary underline">{access.contact_phone}</a>
                }/>
              )}
              {access.meeting_point && <Row icon={MapPin} label={IT ? "Punto d'incontro" : "Meeting point"} value={access.meeting_point}/>}
            </>
          )}
          {access.notes && (
            <div className="mt-4 border-t border-border pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{IT ? "Note dello host" : "Host notes"}</div>
              <p className="text-sm whitespace-pre-wrap" data-testid="guest-access-notes">{access.notes}</p>
            </div>
          )}
          {!access.type && (
            <p className="text-sm text-muted-foreground italic">
              {IT ? "L'host non ha ancora configurato le istruzioni di accesso. Contatta la struttura." : "The host hasn't set up access instructions yet. Please contact the property."}
            </p>
          )}
        </div>

        {/* House rules */}
        <div className="border border-border rounded-lg p-5 bg-card mb-6" data-testid="guest-house-rules">
          <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-3">
            {IT ? "Regole della casa" : "House rules"}
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <BoolBadge ok={rules.smoking} negOk label={rules.smoking ? (IT ? "Fumo consentito" : "Smoking OK") : (IT ? "Vietato fumare" : "No smoking")}/>
            <BoolBadge ok={rules.pets} negOk label={rules.pets ? (IT ? "Animali ammessi" : "Pets OK") : (IT ? "No animali" : "No pets")}/>
            <BoolBadge ok={rules.parties} negOk label={rules.parties ? (IT ? "Feste ammesse" : "Parties OK") : (IT ? "No feste" : "No parties")}/>
            <BoolBadge ok={rules.children} negOk label={rules.children ? (IT ? "Bambini benvenuti" : "Kids welcome") : (IT ? "Solo adulti" : "Adults only")}/>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {rules.check_in_from && <div><span className="text-muted-foreground">{IT ? "Check-in:" : "Check-in:"}</span> {rules.check_in_from}{rules.check_in_to ? ` – ${rules.check_in_to}` : ""}</div>}
            {rules.check_out_by && <div><span className="text-muted-foreground">{IT ? "Check-out entro:" : "Check-out by:"}</span> {rules.check_out_by}</div>}
            {rules.quiet_from && <div className="col-span-2 flex items-center gap-1"><Volume2 className="w-3 h-3 text-muted-foreground"/><span className="text-muted-foreground">{IT ? "Silenzio:" : "Quiet hours:"}</span> {rules.quiet_from} – {rules.quiet_to}</div>}
          </div>
          {rules.custom?.length > 0 && (
            <ul className="mt-4 space-y-1 text-sm border-t border-border pt-3">
              {rules.custom.map((r, i) => <li key={i} className="flex items-start gap-2"><span className="text-primary">•</span>{r}</li>)}
            </ul>
          )}
        </div>

        {/* Contact the host */}
        <div className="text-center pt-4 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2">{IT ? "Serve aiuto?" : "Need help?"}</div>
          <div className="flex justify-center gap-4">
            {p.phone && <a href={`tel:${p.phone}`} className="text-sm text-primary inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{p.phone}</a>}
            {p.email && <a href={`mailto:${p.email}`} className="text-sm text-primary inline-flex items-center gap-1"><Mail className="w-3 h-3"/>{p.email}</a>}
          </div>
        </div>
      </div>
    </div>
  );
}

// EtaCard — collects the guest's expected arrival time and pings the host in
// real-time via /guest-portal/{sid}/eta. Renders only pre-arrival (check-in
// today or later); once the guest has already sent an ETA we surface a
// success confirmation instead of the form. Works for both direct bookings
// and OTA magic-link sessions (backend transparently resolves session_id).
function EtaCard({ booking, sessionId, IT }) {
  const initialEta = (booking?.guest_eta || "").trim();
  const [eta, setEta] = useState(initialEta);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(!!initialEta);
  // Skip the card entirely if the stay has already ended — the ETA card only
  // makes sense in the pre-arrival / arrival-day window.
  try {
    if (booking?.check_out) {
      const co = new Date(booking.check_out);
      if (!isNaN(co.getTime()) && Date.now() > co.getTime() + 24 * 3600 * 1000) return null;
    }
  } catch { /* invalid date — render the card anyway */ }

  const submit = async () => {
    const clean = eta.trim();
    if (clean.length < 2) {
      toast.error(IT ? "Inserisci un orario (es. 15:30 o 'verso le 17')" : "Enter a time (e.g. 15:30 or 'around 17')");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/guest-portal/${sessionId}/eta`, { eta: clean });
      setSent(true);
      toast.success(IT ? "Orario di arrivo inviato all'host" : "Arrival time sent to the host");
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-primary/40 rounded-lg p-5 bg-primary/5 mb-6" data-testid="guest-eta-card">
      <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-2 flex items-center gap-1">
        <Clock className="w-3.5 h-3.5"/>
        {IT ? "Comunica il tuo orario di arrivo" : "Share your arrival time"}
      </div>
      {sent ? (
        <div className="flex items-start gap-2 text-sm" data-testid="guest-eta-sent">
          <Check className="w-4 h-4 text-sage mt-0.5 shrink-0"/>
          <div>
            <div className="font-medium">{IT ? "Grazie! L'host è stato avvisato." : "Thanks! The host has been notified."}</div>
            {/^([01]?\d|2[0-3]):([0-5]\d)$/.test(eta.trim()) ? (
              <div className="text-xs text-muted-foreground mt-0.5">
                {IT ? "Il tuo arrivo previsto: " : "Your expected arrival: "}
                <span className="font-mono text-primary font-semibold">{eta.trim()}</span>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mt-0.5">
                {IT ? "Orario comunicato: " : "Time shared: "}<span className="font-mono text-primary">{eta}</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setSent(false)}
              className="text-xs text-primary underline mt-2"
              data-testid="guest-eta-edit-btn"
            >
              {IT ? "Modifica" : "Update"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            {IT
              ? "Anche un orario indicativo aiuta l'host a organizzare al meglio la tua accoglienza."
              : "Even an approximate time helps the host arrange the best welcome for you."}
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={eta}
              onChange={(e) => setEta(e.target.value)}
              placeholder={IT ? "es. 15:30 o 'verso le 17'" : "e.g. 15:30 or 'around 17'"}
              maxLength={32}
              disabled={busy}
              data-testid="guest-eta-input"
              className="flex-1 min-w-[180px] px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            />
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="guest-eta-submit-btn"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-all lift disabled:opacity-50 font-semibold"
            >
              <Send className="w-4 h-4"/>
              {busy ? (IT ? "Invio…" : "Sending…") : (IT ? "Invia" : "Send")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function FreeCancelCard({ booking, sessionId, IT, onCancelled }) {
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const fc = booking?.free_cancel;
  const cancelled = booking?.status === "cancelled" || booking?.payment_status === "cancelled";

  if (cancelled) {
    return (
      <div className="border border-destructive/40 rounded-lg p-5 bg-destructive/5 mb-6" data-testid="guest-cancelled-banner">
        <div className="flex items-center gap-2 mb-1">
          <Ban className="w-4 h-4 text-destructive"/>
          <div className="font-semibold text-destructive">{IT ? "Prenotazione cancellata" : "Booking cancelled"}</div>
        </div>
        <p className="text-xs text-muted-foreground">{IT ? "Nessun addebito è stato effettuato sulla tua carta." : "No charge has been made to your card."}</p>
      </div>
    );
  }

  if (!fc) return null;

  const deadlineDate = new Date(fc.deadline);
  const secondsLeft = Math.max(0, Math.floor((deadlineDate.getTime() - now) / 1000));
  const days = Math.floor(secondsLeft / 86400);
  const hours = Math.floor((secondsLeft % 86400) / 3600);
  const mins = Math.floor((secondsLeft % 3600) / 60);
  const open = secondsLeft > 0;

  const cancel = async () => {
    const willCharge = !open;
    const amt = Number(fc.late_cancel_penalty || 0).toFixed(2);
    const msg = willCharge
      ? (IT
          ? `Sei fuori dalla finestra di cancellazione gratuita.\nProseguendo verrà addebitata subito la penale di € ${amt} sulla carta a garanzia.\n\nConfermi?`
          : `You're past the free-cancellation window.\nProceeding will immediately charge the € ${amt} penalty to the card on file.\n\nConfirm?`)
      : (IT
          ? "Confermi la cancellazione della prenotazione? Nessun addebito verrà effettuato."
          : "Confirm cancellation? No charge will be made to your card.");
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${sessionId}/cancel`);
      if (r.data?.charged) {
        toast.success(IT ? `Cancellata · addebitati € ${Number(r.data.amount).toFixed(2)}` : `Cancelled · charged € ${Number(r.data.amount).toFixed(2)}`);
      } else {
        toast.success(IT ? "Prenotazione cancellata" : "Booking cancelled");
      }
      onCancelled?.();
    } catch (e) {
      toast.error(errMsg(e, "Error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`border rounded-lg p-5 mb-6 ${open ? "border-sage/40 bg-sage/5" : "border-destructive/40 bg-destructive/5"}`} data-testid="guest-free-cancel-card">
      <div className={`uppercase tracking-[0.2em] text-[10px] font-semibold mb-2 flex items-center gap-1 ${open ? "text-sage" : "text-destructive"}`}>
        {open ? <ShieldCheck className="w-3.5 h-3.5"/> : <ShieldAlert className="w-3.5 h-3.5"/>}
        {open ? (IT ? "Cancellazione gratuita" : "Free cancellation") : (IT ? "Finestra scaduta" : "Window expired")}
      </div>
      {open ? (
        <>
          <div className="text-sm mb-1">
            {IT
              ? <>Puoi cancellare gratuitamente entro il <strong>{deadlineDate.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong></>
              : <>You can cancel free of charge by <strong>{deadlineDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}</strong></>}
          </div>
          <div className="text-xs text-muted-foreground mb-4" data-testid="guest-cancel-countdown">
            {IT ? "Tempo rimanente:" : "Time left:"}{" "}
            <span className="font-mono text-sage font-semibold">
              {days > 0 ? `${days}g ` : ""}{String(hours).padStart(2,"0")}h {String(mins).padStart(2,"0")}m
            </span>
          </div>
          <button
            type="button" onClick={cancel} disabled={busy}
            data-testid="guest-cancel-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-destructive/50 bg-destructive/5 text-destructive hover:bg-destructive hover:text-white transition-all lift disabled:opacity-50"
          >
            <Ban className="w-4 h-4"/>
            {busy ? (IT ? "Cancellazione…" : "Cancelling…") : (IT ? "Cancella prenotazione" : "Cancel booking")}
          </button>
        </>
      ) : (
        <>
          <div className="text-sm mb-2">
            {IT
              ? <>La finestra di cancellazione gratuita è scaduta il <strong>{deadlineDate.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong>.</>
              : <>The free-cancellation window ended on <strong>{deadlineDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}</strong>.</>}
          </div>
          <div className="text-xs text-muted-foreground mb-4 leading-relaxed">
            {IT
              ? <>Puoi comunque cancellare la prenotazione, ma verrà addebitata la penale di <strong className="text-destructive">€ {Number(fc.late_cancel_penalty || 0).toFixed(2)}</strong> sulla carta a garanzia, come da politica accettata al momento della prenotazione.</>
              : <>You can still cancel, but the penalty of <strong className="text-destructive">€ {Number(fc.late_cancel_penalty || 0).toFixed(2)}</strong> will be charged to the guarantee card, as per the policy accepted at booking time.</>}
          </div>
          <button
            type="button" onClick={cancel} disabled={busy}
            data-testid="guest-cancel-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-destructive bg-destructive/10 text-destructive hover:bg-destructive hover:text-white transition-all lift disabled:opacity-50"
          >
            <Ban className="w-4 h-4"/>
            {busy ? (IT ? "Addebito in corso…" : "Charging…") : (IT ? `Cancella e paga € ${Number(fc.late_cancel_penalty || 0).toFixed(2)}` : `Cancel and pay € ${Number(fc.late_cancel_penalty || 0).toFixed(2)}`)}
          </button>
        </>
      )}
    </div>
  );
}


function WelcomeSeedCard({ city, authedGuest, IT, guestName }) {
  const [hunts, setHunts] = useState([]);
  const [claimed, setClaimed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!city) return;
    api.get(`/hunts/by-city/${encodeURIComponent(city)}`)
      .then((r) => setHunts(r.data || []))
      .catch(() => setHunts([]));
  }, [city]);

  const hunt = hunts[0];
  if (!hunt) return null;
  const firstName = (guestName || "").split(" ")[0] || (IT ? "viaggiatore" : "traveller");

  const claim = async () => {
    if (!authedGuest) { window.location.href = "/guest/auth"; return; }
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/hunts/${hunt.id}/claim-welcome`, {});
      setClaimed(r.data.welcome_stop);
      toast.success(IT ? "Regalo di benvenuto sbloccato!" : "Welcome gift unlocked!");
    } catch (e) {
      setError(e?.response?.data?.detail || (IT ? "Errore" : "Error"));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="border-2 border-primary/50 rounded-lg p-8 mb-6 relative overflow-hidden shadow-lg"
      data-testid="welcome-seed-card"
      style={{
        backgroundImage: hunt.hero_image
          ? `linear-gradient(135deg, rgba(60,40,20,0.85) 0%, rgba(197,90,60,0.75) 100%), url(${hunt.hero_image})`
          : undefined,
        backgroundSize: "cover", backgroundPosition: "center", color: "#f5e9cf",
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-6 h-6 text-[#f5e9cf]"/>
        <div className="uppercase tracking-[0.3em] text-[10px] font-semibold opacity-90">
          {IT ? "Esperienza esclusiva della struttura" : "Exclusive host experience"}
        </div>
      </div>
      <h2 className="font-display italic text-4xl md:text-5xl mb-3 leading-tight">
        {IT ? "Benvenuto" : "Welcome"} {firstName},
      </h2>
      <p className="font-display italic text-lg md:text-xl max-w-2xl opacity-95 leading-relaxed mb-4">
        {IT
          ? `l'host ha preparato per te una sfida indimenticabile: `
          : `your host has prepared an unforgettable challenge: `}
        <span className="not-italic font-semibold">«{hunt.title}»</span>
        {IT
          ? `. Segui la Mappa del Tesoro di ${city}, scopri sette segreti e conquista il premio finale.`
          : `. Follow the Treasure Map of ${city}, discover seven secrets, and win the final prize.`}
      </p>
      {claimed && (
        <div className="border border-emerald-300/60 bg-emerald-500/20 rounded p-3 mb-4">
          <div className="text-xs uppercase tracking-widest text-emerald-100 flex items-center gap-1 mb-1">
            <Check className="w-3 h-3"/> {IT ? "Prima tappa sbloccata" : "First stop unlocked"}
          </div>
          <div className="font-display italic text-xl">{claimed.title}</div>
          <div className="text-sm opacity-90 italic mt-1">{claimed.teaser}</div>
        </div>
      )}
      {error && <div className="text-xs bg-white/20 rounded px-2 py-1 mb-3">{error}</div>}
      <div className="flex flex-wrap gap-3 mt-2">
        {!claimed && (
          <button onClick={claim} disabled={busy} data-testid="claim-welcome-btn"
            className="inline-flex items-center gap-2 text-sm px-6 py-3 rounded-full bg-[#f5e9cf] text-[#3a2a17] hover:bg-white transition-all lift disabled:opacity-50 font-semibold">
            <Gift className="w-4 h-4"/>
            {busy ? "…" : (authedGuest ? (IT ? "Riscatta il regalo di benvenuto" : "Claim welcome gift") : (IT ? "Iscriviti gratis per riscattare" : "Sign up free to claim"))}
          </button>
        )}
        <a href={`/caccia-al-tesoro/${hunt.id}`} data-testid="hunt-explore-link"
          className="inline-flex items-center gap-2 text-sm px-6 py-3 rounded-full border-2 border-[#f5e9cf] text-[#f5e9cf] hover:bg-[#f5e9cf] hover:text-[#3a2a17] transition-all font-semibold">
          <Map className="w-4 h-4"/>
          {IT ? "Apri la Mappa del Tesoro" : "Open the Treasure Map"}
          <ArrowUpRight className="w-3 h-3"/>
        </a>
      </div>
    </div>
  );
}

